import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { TipoTransacao } from '../../../enums/TipoTransacao.enum';
import { TipoDeDespesa } from '../../../enums/TipoDespesa.enum';
import { Moeda } from '../../../enums/moeda.enum';
import { DespesaDTO } from '../../../models/gerenciar-contas/despesa.model';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { DespesaService } from '../../../services/movimento/despesa.service';
import { ContaService } from '../../../services/conta/conta.service';
import { CotacaoGuardService } from '../../../services/cotacao/cotacao-guard.service';
import { CotacaoService } from '../../../services/cotacao/cotacao.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../models/configuracoes/empresa.model';
import { moedasCompativeis } from '../../../utils/moeda-pareamento.util';
import { toLocalISOString } from '../../../shared/utils/date-utils';
import { ValoresRecebidos } from '../../../shared/components/forma-pagamento-cards/forma-pagamento-cards.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nova-despesa-edit',
  templateUrl: './nova-despesa-edit.component.html',
  styleUrls: ['./nova-despesa-edit.component.scss'],
  providers: [MessageService],
})
export class NovaDespesaEditComponent implements OnInit, OnDestroy {
  form: DespesaDTO = this.createEmptyForm();
  contas: ContaDTO[] = [];
  tipoDespesaOptions = Object.values(TipoDeDespesa).map((value) => ({
    label: value,
    value,
  }));
  moedas: { label: string; value: any; flagUrl?: string }[] = [];
  idEdit?: number;
  contasOptions: { label: string; value: number; flagUrl?: string }[] = [];
  clientes: any[] = [];
  empresa: EmpresaConfigDTO | null = null;
  // Strings formatadas para cada campo de valor (evita que ngModel sobrescreva a mascara)
  // valor / valorRecebidoNaConta seguem com mascara local; os 4 valorRecebidoEm{X}
  // sao agora gerenciados pelo sub-componente app-forma-pagamento-cards.
  valorStrs: Record<string, string> = {
    valor: '',
    valorRecebidoNaConta: '',
  };

  /** Moeda padrao da empresa (ISO) usada pelo sub-componente de cards. */
  moedaEmpresa: 'PYG' | 'BRL' | 'USD' | 'ARS' | undefined;

  /** Cotacoes vigentes (USD->X). cotPesoGuarani gateia o card ARS. */
  cotPYG = 0;
  cotBRL = 0;
  cotPesoGuarani = 0;
  /** Cotacao direta BRL->PYG (1 BRL = N PYG). 0 = triangulacao via USD. */
  cotRealGuarani = 0;

  /** Estado dos valores dos cards (two-way binding com app-forma-pagamento-cards). */
  formValores: ValoresRecebidos = {
    valorRecebidoEmGuarani: 0,
    valorRecebidoEmReal: 0,
    valorRecebidoEmDolar: 0,
    valorRecebidoEmPeso: 0,
  };

  // Objetos de traducao
  texts = {
    titulo: '',
    observacao: '',
    tipo_despesa: '',
    conta_origem: '',
    moeda: '',
    conta_destino: '',
    valor: '',
    valor_recebido_na_conta: '',
    valor_recebido_em_guarani: '',
    valor_recebido_em_dolar: '',
    valor_recebido_em_real: '',
    pessoa_fornecedor: ''
  };

  placeholders = {
    observacao: '',
    selecione: '',
    valor: '',
    valor_recebido_na_conta: '',
    valor_recebido_em_guarani: '',
    valor_recebido_em_dolar: '',
    valor_recebido_em_real: '',
    selecionar_pessoa: ''
  };

  botoes = {
    salvar: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private despesaService: DespesaService,
    private contaService: ContaService,
    private cotacaoService: CotacaoService,
    private cotacaoGuard: CotacaoGuardService,
    private pessoaService: PessoaService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      // Re-traduz labels do dropdown de moeda ao trocar idioma
      this.buildMoedaOptions();
    });
    // Monta dropdown ja com fallback (todas as 4 moedas) ate empresa carregar
    this.buildMoedaOptions();
    this.loadContas();
    this.findEmpresa();
    this.loadClientes();
    this.carregarCotacoes();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.idEdit = +id;
      this.loadDespesa(this.idEdit);
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('nova-despesa-edit.texts.titulo'),
      observacao: this.translate.instant('nova-despesa-edit.labels.observacao'),
      tipo_despesa: this.translate.instant('nova-despesa-edit.labels.tipo_despesa'),
      conta_origem: this.translate.instant('nova-despesa-edit.labels.conta_origem'),
      moeda: this.translate.instant('nova-despesa-edit.labels.moeda'),
      conta_destino: this.translate.instant('nova-despesa-edit.labels.conta_destino'),
      valor: this.translate.instant('nova-despesa-edit.labels.valor'),
      valor_recebido_na_conta: this.translate.instant('nova-despesa-edit.labels.valor_recebido_na_conta'),
      valor_recebido_em_guarani: this.translate.instant('nova-despesa-edit.labels.valor_recebido_em_guarani'),
      valor_recebido_em_dolar: this.translate.instant('nova-despesa-edit.labels.valor_recebido_em_dolar'),
      valor_recebido_em_real: this.translate.instant('nova-despesa-edit.labels.valor_recebido_em_real'),
      pessoa_fornecedor: this.translate.instant('nova-despesa-edit.labels.pessoa_fornecedor')
    };

    this.placeholders = {
      observacao: this.translate.instant('nova-despesa-edit.placeholders.observacao'),
      selecione: this.translate.instant('nova-despesa-edit.placeholders.selecione'),
      valor: this.translate.instant('nova-despesa-edit.placeholders.valor'),
      valor_recebido_na_conta: this.translate.instant('nova-despesa-edit.placeholders.valor_recebido_na_conta'),
      valor_recebido_em_guarani: this.translate.instant('nova-despesa-edit.placeholders.valor_recebido_em_guarani'),
      valor_recebido_em_dolar: this.translate.instant('nova-despesa-edit.placeholders.valor_recebido_em_dolar'),
      valor_recebido_em_real: this.translate.instant('nova-despesa-edit.placeholders.valor_recebido_em_real'),
      selecionar_pessoa: this.translate.instant('nova-despesa-edit.placeholders.selecione')
    };

    this.botoes = {
      salvar: this.translate.instant('nova-despesa-edit.botoes.salvar')
    };
  }

  loadClientes(): void {
    this.pessoaService.get('/pessoas/buscar?categoriaCliente=true').subscribe((res) => {
      this.clientes = res.content.map((c: any) => ({ id: c.id, nome: c.nome }));
    });
  }

  get clientesOptions(): { id: number; nome: string }[] {
    return this.clientes;
  }

  formatContaOptions(): void {
    this.contasOptions = this.contas
      .filter((c) => c.id !== undefined)
      .map((c) => ({
        label: (c.numeroConta as any) == 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
        value: c.id as number,
        flagUrl: this.getFlagUrl(c.moeda),
      }));

    // Ordenar: conta DINHEIRO da moeda padrao da empresa aparece primeiro
    if (this.empresa?.moeda) {
      const moedaPadrao = this.asEnumString(this.empresa.moeda);
      this.contasOptions.sort((a, b) => {
        const contaA = this.contas.find(c => c.id === a.value);
        const contaB = this.contas.find(c => c.id === b.value);
        const isTopA = contaA && this.asEnumString(contaA.tipoConta) === 'DINHEIRO' && this.asEnumString(contaA.moeda) === moedaPadrao ? 1 : 0;
        const isTopB = contaB && this.asEnumString(contaB.tipoConta) === 'DINHEIRO' && this.asEnumString(contaB.moeda) === moedaPadrao ? 1 : 0;
        return isTopB - isTopA;
      });
    }
  }

  findEmpresa(): void {
    this.empresaService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (response) => {
        this.empresa = response;
        // Re-monta dropdown de moeda agora que `moedasPermitidas` reflete a
        // moeda padrao da empresa (sem fallback para todas as 4).
        this.buildMoedaOptions();
        // Deriva moeda ISO para o sub-componente de cards
        this.moedaEmpresa = this.derivarMoedaIsoEmpresa();
        // Re-ordenar contas caso ja tenham sido carregadas antes da empresa
        if (this.contasOptions.length > 0) {
          this.formatContaOptions();
        }
      },
      error: () => {
        // Mantem fallback (todas as 4) quando empresa nao carrega
        this.buildMoedaOptions();
      }
    });
  }

  /**
   * Converte a moeda da empresa (enum GUARANI/REAL/DOLAR/PESO) para codigo
   * ISO (PYG/BRL/USD/ARS) consumido pelo sub-componente de cards. Mesmo
   * mapeamento usado em vendas.component (linha ~770).
   */
  private derivarMoedaIsoEmpresa(): 'PYG' | 'BRL' | 'USD' | 'ARS' | undefined {
    const moeda: any = this.empresa?.moeda;
    if (!moeda) return undefined;
    // Preferir codigoOperacao quando disponivel (vem do backend)
    if (moeda.codigoOperacao) {
      return moeda.codigoOperacao as 'PYG' | 'BRL' | 'USD' | 'ARS';
    }
    const enumStr = this.asEnumString(moeda);
    if (enumStr === 'GUARANI') return 'PYG';
    if (enumStr === 'REAL') return 'BRL';
    if (enumStr === 'DOLAR') return 'USD';
    if (enumStr === 'PESO') return 'ARS';
    return undefined;
  }

  /**
   * Carrega as cotacoes vigentes (dolarGuarani, dolarReal, pesoGuarani) do
   * endpoint canonico /cotacao/ultimaCotacao. Mesmo padrao do PDV / fatura-view.
   */
  private carregarCotacoes(): void {
    this.cotacaoService.get('/cotacao/ultimaCotacao').subscribe({
      next: (res: any) => {
        if (!res) return;
        this.cotPYG = res.dolarGuarani || 0;
        this.cotBRL = res.dolarReal || 0;
        this.cotPesoGuarani = res.pesoGuarani || 0;
        this.cotRealGuarani = res.realGuarani || 0;
      },
      error: () => {
        // Mantem 0 -> sub-componente desabilita ARS por defesa em profundidade
      },
    });
  }

  getFlagUrl(moeda: any): string {
    const moedaStr = this.asEnumString(moeda);
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  private asEnumString(v: any): string {
    if (!v) return '';
    if (typeof v === 'object' && 'enum' in v) return String(v.enum).toUpperCase();
    if (typeof v === 'object' && 'value' in v) return String(v.value).toUpperCase();
    return String(v ?? '').toUpperCase();
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Fallback: enquanto a
   * empresa nao carregou, retorna todas as 4 para nao bloquear UI.
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }

  /**
   * Monta o dropdown de moeda aplicando o filtro de pareamento.
   * Edge case legacy: ao editar despesa com moeda agora bloqueada,
   * mantem a moeda atual visivel para nao "sumir" o valor selecionado.
   */
  private buildMoedaOptions(): void {
    const permitidas = this.moedasPermitidas;
    const moedaAtual = this.form?.moedaDeOrigem
      ? this.asEnumString((this.form.moedaDeOrigem as any).value ?? this.form.moedaDeOrigem)
      : null;
    this.moedas = Object.values(Moeda)
      .filter(m => {
        const codigo = this.asEnumString(m);
        return permitidas.has(codigo) || codigo === moedaAtual;
      })
      .map(moeda => ({
        label: this.translate.instant(`enums.moeda.${moeda.toLowerCase()}`),
        value: moeda,
        flagUrl: this.getFlagUrl(moeda),
      }));
  }

  loadDespesa(id: number): void {
    this.despesaService.getById(`/movimento/${id}`).subscribe({
      next: (response) => {
        this.form = {
          ...response,
          conta_id: response.contaDTO?.id,
          contaDestino_id: response.contaDestinoDTO?.id,
          moedaDeOrigem: response.moedaDeOrigem,
          pessoa_id: response.pessoa?.id ?? response.pessoa_id ?? null,
        };
        // Popula valores dos cards de pagamento a partir da despesa carregada
        this.formValores = {
          valorRecebidoEmGuarani: response.valorRecebidoEmGuarani || 0,
          valorRecebidoEmReal: response.valorRecebidoEmReal || 0,
          valorRecebidoEmDolar: response.valorRecebidoEmDolar || 0,
          valorRecebidoEmPeso: (response as any).valorRecebidoEmPeso || 0,
        };
        // Garante que a moeda atual (legacy) entre na lista mesmo se bloqueada
        this.buildMoedaOptions();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('nova-despesa-edit.mensagens.falha_carregar'),
        });
        this.router.navigate(['/nova-despesa']);
      },
    });
  }

  loadContas(): void {
    this.contaService.get('/conta').subscribe({
      next: (response) => {
        this.contas = response.content;
        this.formatContaOptions();
      },
      error: (err) => {
        console.error('Erro ao carregar contas:', err);
      },
    });
  }

  submit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.loadCotacao(() => {
      const body: DespesaDTO = {
        ...this.form,
        observacao: this.form.observacao?.trim() || null,
        tipoDeTransacao: TipoTransacao.DESPESA,
        cancelado: false,
        baixas: [],
        emissao: toLocalISOString(this.form.emissao ? new Date(this.form.emissao as any) : new Date()),
        valorRecebidoNaConta: this.form.valorRecebidoNaConta,
        // Valores recebidos por moeda saem do sub-componente de cards (FormValores)
        valorRecebidoEmGuarani: this.formValores.valorRecebidoEmGuarani,
        valorRecebidoEmDolar: this.formValores.valorRecebidoEmDolar,
        valorRecebidoEmReal: this.formValores.valorRecebidoEmReal,
        valorRecebidoEmPeso: this.formValores.valorRecebidoEmPeso,
      } as DespesaDTO;

      this.despesaService.put(`/movimento/${this.idEdit}`, body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('nova-despesa-edit.mensagens.editada_sucesso'),
          });
          this.router.navigate(['/despesas']);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('nova-despesa-edit.mensagens.falha_editar'),
          });
        },
      });
    });
  }


  /** Garante a cotacao do dia (solicita ao usuario se nao houver) antes de salvar. Cancelou: nao salva. */
  loadCotacao(callback: () => void): void {
    if (!this.form.moedaDeOrigem) {
      callback();
      return;
    }
    this.cotacaoGuard.garantirCotacao().then((cotacao) => {
      if (!cotacao?.id) {
        return;
      }
      this.form.cotacao_id = cotacao.id;
      callback();
    });
  }


  getSymbolMoeda(): string {
    switch (this.form.moedaDeOrigem) {
      case Moeda.REAL:
        return 'R$';
      case Moeda.DOLAR:
        return '$';
      case Moeda.GUARANI:
        return 'Gs';
      default:
        return '';
    }
  }

  /**
   * Formata `valor` e `valorRecebidoNaConta` conforme a moeda da despesa.
   * Os 4 campos `valorRecebidoEm{X}` foram migrados para o sub-componente
   * `app-forma-pagamento-cards` (handler interno) e nao passam mais por aqui.
   */
  formataCampoValor(event: any, campo: keyof Pick<DespesaDTO, 'valor' | 'valorRecebidoNaConta'>): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');

    const moedaDoCampo = (this.form.moedaDeOrigem as Moeda) || Moeda.GUARANI;

    if (moedaDoCampo === Moeda.GUARANI) {
      const intVal = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      (this.form as any)[campo] = intVal || null;
      this.valorStrs[campo] = intVal
        ? intVal.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : '';
    } else if (moedaDoCampo === Moeda.DOLAR) {
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / 100;
      (this.form as any)[campo] = cents ? val : null;
      this.valorStrs[campo] = cents
        ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
        : '';
    } else if (moedaDoCampo === Moeda.PESO) {
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / 100;
      (this.form as any)[campo] = cents ? val : null;
      this.valorStrs[campo] = cents
        ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)
        : '';
    } else {
      // REAL
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / 100;
      (this.form as any)[campo] = cents ? val : null;
      this.valorStrs[campo] = cents
        ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
        : '';
    }
  }

  validateForm(): boolean {
    const requiredFields: (keyof DespesaDTO)[] = [
      'observacao',
      'valor',
      'moedaDeOrigem',
      'conta_id',
      'contaDestino_id',
    ];
    for (const field of requiredFields) {
      if (!this.form[field]) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: `O campo ${field} é obrigatório.`,
        });
        return false;
      }
    }
    return true;
  }

  private createEmptyForm(): DespesaDTO {
    return {
      observacao: '',
      tipoDeTransacao: TipoTransacao.DESPESA,
      tipoDespesa: undefined,
      cancelado: false,
      valor: 0,
      cotacao_id: 0,
      conta_id: undefined,
      pessoa_id: null,
      moedaDeOrigem: undefined,
      contaDestino_id: undefined,
      valorRecebidoNaConta: 0,
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmReal: 0,
      emissao: undefined,
      baixas: [],
      montante: null,
      moeda: undefined,
    };
  }

}
